import { Injectable, Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { of as ObservableOf } from 'rxjs/observable/of';
import { _throw as ObservableThrow } from 'rxjs/observable/throw';
import { forkJoin as RxForkJoin } from 'rxjs/observable/forkJoin';
import { catchError, filter, map, take, mergeMap, flatMap, switchMap, tap, finalize } from 'rxjs/operators';
import {
  isNil as _isNil,
  filter as _filter,
  forEach as _forEach,
  find as _find,
  isEmpty as _isEmpty,
  includes as _include,
  get as _get,
} from 'lodash';
import {
  DateTimeUtils,
  DateDigitStyle,
  DateTimeOutput,
  DateTimePadding,
  DateSeparator,
  LocalStorageUtils,
  UrlUtils,
  StringUtils,
} from '@blcloud/bl-common';
import {
  IGenericFlagMgt,
  ILoginUserEmployeeBindModel,
  IMasterAutoInsert,
  IMasterPrecheck,
  ISfSlip,
  IAirisInquiryResultData,
  IServiceAvailabilityInfo,
  IOemCustomize,
  IDiagnosisResultInfo,
  IActualTallyCondition,
  IActualSearchSetting,
  IOrganizationSelectInfo,
  IOrganizationInformation,
  IEstimatesRequestSearchReq,
  IEstimatesRequest,
  IYahooApplicationIdSetting,
  IOnlineCompanySetting,
  IYahooOrderInfo,
  IContractCompanyInfo,
  IBiToolTask,
  INotificationMessage,
  IOrderPartsCart,
  IPartsOrderFavoriteDisplay,
  IOrderMemoAutoPrintErrorInfo,
  IChatListDisplayInfo,
  ICommonGenericFlagMgt,
  IBillSetting,
  IReportLayoutSetting,
  IExportPatternInfo,
  IBillingAccRecBalExtractCondition,
  IGenericPrintInstruction,
  IPrintPositionSetting,
  IOpeHistory,
  IAccpayBalanceExtractCondition,
} from '@blcloud/bl-datamodel';
import {
  GenericFlagMgtResource,
  MasterAutoInsertResource,
  MasterPrecheckResource,
  SfSlipResource,
  AirisInquiryResultDataResource,
  ReceptionInfoResource,
  GlassSlipInfoResource,
  DiagnosisResultInfoResource,
  ActualSearchSettingResource,
  CustomerInfoResource,
  EstimatesRequestResource,
  YahooOrderInfoResource,
  YahooApplicationIdSettingResource,
  OnlineCompanySettingResource,
  ContractCompanyInfoResource,
  BlPayApplyInfoResource,
  BiToolTaskResource,
  NotificationMessageResource,
  ReserveSearchResource,
  OrderPartsCartResource,
  PartsOrderFavoriteDisplayResource,
  OrderMemoAutoPrintErrorInfoResource,
  YahooStockQuantityInfoResource,
  ChatListDisplayInfoResource,
  CommonGenericFlagMgtResource,
  BillSettingResource,
  ReportLayoutSettingResource,
  ExportPatternInfoResource,
  GenericPrintInstructionResource,
  PrintPositionSettingResource,
  OpeHistoryResource,
} from '@blcloud/bl-ng-resource';
import { NotificationLevel } from '@blcloud/bl-datamodel/enum/notification/notification-level';
import { NotificationGuideDiv } from '@blcloud/bl-datamodel/enum/notification/notification-guide-div';
import { ManagementAnalysisSideMenu } from '@blcloud/bl-datamodel/enum/managementreport/management-analysis-side-menu';
import { ManagementAnalysisItem } from '@blcloud/bl-datamodel/enum/managementreport/management-analysis-item';
import { ComparedToLastYear } from '@blcloud/bl-datamodel/enum/managementreport/compared-to-last-year';
import { DisplayUnit } from '@blcloud/bl-datamodel/enum/managementreport/display-unit';
import { ActualTallyOrganizationDiv } from '@blcloud/bl-datamodel/enum/managementreport/actual-tally-organization-div';
import { OrganizationSumSetting } from '@blcloud/bl-datamodel/enum/managementreport/organization-sum-setting';
import { CompanyConst } from '@blcloud/bl-datamodel/const/company';
import { MasterCheckResultDiv } from '@blcloud/bl-datamodel/enum/bizcmn/master-check-result-div';
import { MasterAutoInsertResultDiv } from '@blcloud/bl-datamodel/enum/bizcmn/master-auto-insert-result-div';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';
import { VehicleEnteringStatus } from '@blcloud/bl-datamodel/enum/stock/vehicle-entering-status';
import { BizNegoStatus } from '@blcloud/bl-datamodel/enum/deals/biz-nego-status';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { CommonConst } from '@blcloud/bl-datamodel/const/common';
import {
  BlApiConditionHelper,
  BlApiQueryParams,
  BlApiSearchCondition,
  BlApiErrorInfo,
  HttpStatus,
  BlListResponseBody,
  BlConditionInfo,
  BlConditionType,
} from '@blcloud/bl-ng-common';
import {
  IGenericPrintModalData,
  LoginResourceService, OemCustomizeService, OrganizationInformationResourceService,
  PageId,
  ScreenNotifyParam,
  ScreenNotifyService
} from '@blcloud/bl-ng-share-module';
import { ReadOrUnreadSearchDiv } from '@blcloud/bl-datamodel/enum/sales/read-or-unread-search-div';
import { AffiliatedStoreSuppliersDiv } from '@blcloud/bl-datamodel/enum/factoring/affiliated-store-suppliers-div';
import { ApplyStatus } from '@blcloud/bl-datamodel/enum/contract/apply-status';
import { ContractApplyStatusDiv } from '@blcloud/bl-datamodel/enum/contract/contract-apply-status-div';
import {
  LocalStorageKey,
  DepositScreenType,
  PaymentScreenType,
  BiToolDetailAnalysisService
} from './biz-menu-page.const';
import { OrdermemoResource } from './biz-menu-page.define';
import { OrderMemoAutoPrintDiv } from '@blcloud/bl-datamodel/enum/bizcmn/order-memo-auto-print-div';
import {
  CreditExportConditionModalComponent
} from '../../feature/export-modal/credit-export-condition-modal/credit-export-condition-modal.component';
import { BlDialogService, BlLoadingService, BlModalService, ModalReason, ModalSize } from '@blcloud/bl-ng-ui-component';
import {
  DebtExportConditionModalComponent
} from '../../feature/export-modal/debt-export-condition-modal/debt-export-condition-modal.component';
import { ReportOutputDiv } from '@blcloud/bl-datamodel/enum/common/report-output-div';
import { ExportRequestConfirmComponent } from '../../feature/export-modal/export-request-confirm/export-request-confirm.component';
import { OpeDiv } from '@blcloud/bl-datamodel/enum/bizcmn/ope-div';
import { FunctionCategoryDiv } from '@blcloud/bl-datamodel/enum/bizcmn/function-category-div';
import { ExportInfoDiv, ExportInfoDivMap } from '@blcloud/bl-datamodel/enum/output/export-info-div';
import { empty } from 'rxjs/observable/empty';
import { BalanceType } from '../../feature/export-modal/credit-export-condition-modal/credit-export-condition-modal.define';
import { AccPayBalanceDataOutputType } from '@blcloud/bl-datamodel/enum/bizcmn/acc-pay-balance-data-output-type';

export interface IDiagnosisResultInfoEx extends IDiagnosisResultInfo {
  amountUnit?: string;
}

/**
 * 業務メニュー画面用サービス
 */
@Injectable()
export class BizMenuPageService {

  /** 権限配列を保持 */
  private availabilitiesCache: string[] = [];

  /** マスターデータ修復結果 */
  private masterFixedInfo$ = new BehaviorSubject<IMasterAutoInsert | null>(null);

  /** カート件数の通知ID */
  private purchasedPartsToastId: number = null;

  /** 出力情報名称 */
  private exportInfoName = ExportInfoDivMap[ExportInfoDiv.BillingAccRecBalList];

  // 帳票用途コード
  private reportUseCode = 3;

  constructor(
    private genericFlagMgtResource: GenericFlagMgtResource,
    private masterPrecheckResource: MasterPrecheckResource,
    private masterAutoInsertResource: MasterAutoInsertResource,
    private sfSlipResource: SfSlipResource,
    private airisInquiryResultDataResource: AirisInquiryResultDataResource,
    private receptionInfoResource: ReceptionInfoResource,
    private glassSlipInfoResource: GlassSlipInfoResource,
    private loginResourceService: LoginResourceService,
    private oemCustomizeService: OemCustomizeService,
    private diagnosisResultInfoResource: DiagnosisResultInfoResource,
    private actualSearchSettingResource: ActualSearchSettingResource,
    private organizationInfoService: OrganizationInformationResourceService,
    private customerInfoResource: CustomerInfoResource,
    private estimateRequestResource: EstimatesRequestResource,
    private yahooOrderInfoResource: YahooOrderInfoResource,
    private yahooApplicationIdSettingResource: YahooApplicationIdSettingResource,
    private yahooStockQuantityInfoResource: YahooStockQuantityInfoResource,
    private onlineCompanySettingResource: OnlineCompanySettingResource,
    private contractCompanyInfoResource: ContractCompanyInfoResource,
    private blPayApplyInfoResource: BlPayApplyInfoResource,
    private biToolTaskResource: BiToolTaskResource,
    private notificationMessageResource: NotificationMessageResource,
    private reserveSearchResource: ReserveSearchResource,
    private screenNotifyService: ScreenNotifyService,
    private ordermemoResource: OrdermemoResource,
    private orderPartsCartResource: OrderPartsCartResource,
    private partsOrderFavoriteDisplayResource: PartsOrderFavoriteDisplayResource,
    private orderMemoAutoPrintErrorInfoResource: OrderMemoAutoPrintErrorInfoResource,
    private chatListDisplayInfoResource: ChatListDisplayInfoResource,
    private commonGenericFlagMgtResource: CommonGenericFlagMgtResource,
    private billSettingResource: BillSettingResource,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    private loadingService: BlLoadingService,
    private reportLayoutSettingResource: ReportLayoutSettingResource,
    private exportPatternInfoResource: ExportPatternInfoResource,
    private genericPrintInstructionResource: GenericPrintInstructionResource,
    private printPositionSettingResource: PrintPositionSettingResource,
    private opeHistoryResource: OpeHistoryResource,
  ) { }

  /**
   * マスタデータ不備をチェックし、問題があれば修復リクエストを投げます
   * 結果は、`getMasterFixedInfo()` を監視してください
   */
  fixMasterIfNeed(serviceIdList: string[]): void {
    this.checkMasterData(serviceIdList).subscribe(info => {
      if (info && info.masterCheckResultDiv === MasterCheckResultDiv.NoGood) {
        this.fixMaster(serviceIdList, info.entityClassIdList).subscribe(fixedInfo => {
          this.masterFixedInfo$.next(fixedInfo);
        });
      } else {
        // 問題なし（仕様：チェックAPIでエラーが発生した場合もこちらの処理になります）
        this.masterFixedInfo$.next({ masterAutoInsertResultDiv: MasterAutoInsertResultDiv.NoNeed, messageContents: null });
      }
    });
  }

  /**
   * マスタデータチェック＆修復結果を取得します
   * unsubscribe不要です
   */
  getMasterFixedInfo(): Observable<IMasterAutoInsert> {
    return this.masterFixedInfo$.asObservable().pipe(
      filter(info => info !== null),
      take(1)
    );
  }


  /**
   * ログイン状態判定
   */
  isLogin(loginUserEmployeeBindModel: ILoginUserEmployeeBindModel): boolean {
    return !_isNil(loginUserEmployeeBindModel) &&
      !_isNil(loginUserEmployeeBindModel.blUserId) &&
      !_isNil(loginUserEmployeeBindModel.employeeCode);
  }

  /**
   * ジェネリックフラグ取得
   */
  getGenericFlag(key: string): Observable<IGenericFlagMgt | null> {
    return this.genericFlagMgtResource.getById<IGenericFlagMgt>(key).pipe(
      catchError(err => {
        if (err.status === 404) {
          return ObservableOf(null);
        }
        throw ObservableThrow(err);
      })
    );
  }

  /**
   * ジェネリックフラグ保存
   * @param key キー
   * @param value フラグ値
   * @param isNew 新規登録の場合 `true` を指定してください
   */
  saveGenericFlag(key: string, value: boolean, isNew = false): Observable<IGenericFlagMgt> {
    const flagMgt: IGenericFlagMgt = { genericFlagKey: key, genericFlagValue: value };
    if (isNew) {
      return this.genericFlagMgtResource.post<IGenericFlagMgt>(flagMgt);
    } else {
      return this.genericFlagMgtResource.putByEntity(flagMgt);
    }
  }


  /**
   * マスターデータ不備チェック
   * @param serviceIdList 利用可能なオプションコードリスト
   */
  private checkMasterData(serviceIdList: string[]): Observable<IMasterPrecheck> {
    return this.masterPrecheckResource.post({ serviceIdList }).pipe(
      catchError(err => {
        // 仕様：このAPIは機能への影響が極小とのことで、エラーが発生しても無視します。
        console.error('master precheck error. but continue.', err);
        return ObservableOf(null);
      })
    );
  }

  /**
   * マスターデータ修復リクエスト
   */
  private fixMaster(serviceIdList: string[], entityClassIdList: string[]): Observable<IMasterAutoInsert> {
    return this.masterAutoInsertResource.post({ serviceIdList, entityClassIdList });
  }

  /**
   * 整備下書き伝票削除
   */
  deleteSfSlipDraft(): Observable<ISfSlip> {
    return this.sfSlipResource.delete({
      entityOptions: { operation: 'draft', exPath: 'expired' }
    }).pipe(
      catchError(() => {
        return ObservableOf({});
      })
    );
  }

  /**
   * AIRIS問合せ結果マージ処理
   */
  mergeAirisInquiryResultData(): Observable<IAirisInquiryResultData> {
    const entities = [];
    return this.airisInquiryResultDataResource.put(
      entities,
      { entityOptions: { exPath: 'merge' } }
    ).pipe(
      catchError(() => {
        return ObservableOf({});
      })
    );
  }

  /** リサイクル 車両受付カウント */
  countRecycleVehicleReception(organizationCode: string): Observable<number> {
    const params = new BlApiQueryParams();
    const conditions = new BlApiSearchCondition();

    conditions.addCondition(
      BlApiConditionHelper.makeCondAnd(
        BlApiConditionHelper.makeCondStrEqual('receptOrganizationCode', organizationCode),
        BlApiConditionHelper.makeCondStrEqual('logicalDeleteDiv', LogicalDeleteDiv.Valid),
        BlApiConditionHelper.makeCondStrEqual('productCode', ProductCode.Recycle),
        BlApiConditionHelper.makeCondStrLessThanEqual('vehicleEnteringStatus', VehicleEnteringStatus.NoEntering),
        BlApiConditionHelper.makeCondStrLessThanEqual('bizNegoStatus', BizNegoStatus.During),
      )
    );
    params.setCondition(conditions);

    return this.receptionInfoResource.get<{ totalCount: number }>(
      {
        params,
        entityOptions: {
          exPath: 'count',
        },
      },
    ).pipe(
      map((response) => response ? response.totalCount : 0),
    );
  }

  /** リサイクル 車両入庫カウント */
  countRecycleVehicleWarehousing(organizationCode: string): Observable<number> {
    const params = new BlApiQueryParams();
    const conditions = new BlApiSearchCondition();

    conditions.addCondition(
      BlApiConditionHelper.makeCondAnd(
        BlApiConditionHelper.makeCondStrEqual('receptOrganizationCode', organizationCode),
        BlApiConditionHelper.makeCondStrEqual('logicalDeleteDiv', LogicalDeleteDiv.Valid),
        BlApiConditionHelper.makeCondStrEqual('productCode', ProductCode.Recycle),
        BlApiConditionHelper.makeCondStrGreaterThanEqual('bizNegoStatus', BizNegoStatus.Conclusion),
        BlApiConditionHelper.makeCondStrLessThanEqual('bizNegoStatus', BizNegoStatus.Completion),
        BlApiConditionHelper.makeCondStrGreaterThanEqual('vehicleEnteringStatus', VehicleEnteringStatus.NoEntering),
        BlApiConditionHelper.makeCondStrLessThanEqual('vehicleEnteringStatus', VehicleEnteringStatus.Received),
      )
    );
    params.setCondition(conditions);

    return this.receptionInfoResource.get<{ totalCount: number }>(
      {
        params,
        entityOptions: {
          exPath: 'count',
        },
      },
    ).pipe(
      map((response) => response ? response.totalCount : 0),
    );
  }

  /** リサイクル 見積依頼確認カウント */
  countRecycleEstimateRequestConfirm(organizationCode: string, blTenantId: string): Observable<number> {
    const conditions: IEstimatesRequestSearchReq = {};
    conditions.estimatesRequestSearchSetting = {};
    conditions.estimatesRequestSearchSetting.organizationCode = organizationCode;
    conditions.readOrUnreadSearchDiv = ReadOrUnreadSearchDiv.Unread;
    conditions.readOrUnreadSearchKeyword = blTenantId;
    conditions.estimetesRequestSearchSettingUseFlag = true;
    conditions.isNeedOnlyTotalCount = true;
    return this.estimateRequestResource.post<BlListResponseBody<IEstimatesRequest>>(conditions,
      {
        entityOptions: {
          exPath: 'searchestimatesrequestlistbysetting',
        },
      },
    ).pipe(
      map((response) => response ? response.totalCount : 0),
    );
  }

  /**
   * 硝子下書き伝票件数取得
   * @param slipKind 伝票区分
   */
  glassSlipDraftCount(slipKind: string): Observable<any> {
    const conditions = new BlApiSearchCondition();
    conditions.addCondition(
      BlApiConditionHelper.makeCondAnd(
        BlApiConditionHelper.makeCondStrEqual('slipKind', slipKind),
      ));
    const param = new BlApiQueryParams();
    param.setCondition(conditions);
    return this.glassSlipInfoResource.get({
      params: param,
      entityOptions: { operation: 'draft', exPath: 'count' }
    }).pipe(
      map((response) => response),
    );
  }

  /**
   * サービスIDからプロダクトコードの判別を行う
   *
   * @returns プロダクトコード
   */
  fetchProductCodeByServiceId() {
    return this.loginResourceService.getAllServiceAvailabilityInfo().pipe(
      map(v => v ? v : []),
      map((services: IServiceAvailabilityInfo[]) => {
        // プロダクトコード決定優先順位
        // 先頭の定義ほど、決定順位が高くなります
        const ProductDefList = [
          { serviceId: ContractConst.SERVICEID_PRODUCT_PARTSMAN, productCode: ProductCode.Partsman },
          { serviceId: ContractConst.SERVICEID_PRODUCT_RECYCLE, productCode: ProductCode.Recycle },
          { serviceId: ContractConst.SERVICEID_OPT_MAINTENANCE_BASE, productCode: ProductCode.SuperFrontman },
          { serviceId: ContractConst.SERVICEID_OPT_REPAIR_BASE, productCode: ProductCode.SuperFrontman },
          { serviceId: ContractConst.SERVICEID_OPT_CMN_CARSALES_ESTIMATE_ORDER, productCode: ProductCode.CarSales },
          { serviceId: ContractConst.SERVICEID_PRODUCT_GLASS, productCode: ProductCode.Glass },
        ];
        const def = ProductDefList.find(d => !!services.find(s => s.serviceId === d.serviceId));
        if (def) {
          return def.productCode;
        }
        return ProductCode.SuperFrontman;
      })
    );
  }

  /**
   * サービスIDから権限のあるプロダクトコードのリストを取得する
   * 優先順位の高いプロダクトコードが先頭となる
   *
   * @returns プロダクトコードのリスト
   */
  fetchProductCodeListByServiceId() {
    return this.loginResourceService.getAllServiceAvailabilityInfo().pipe(
      map(v => v ? v : []),
      map((services: IServiceAvailabilityInfo[]) => {
        // プロダクトコード決定優先順位
        // 先頭の定義ほど、決定順位が高くなります
        const ProductDefList = [
          { serviceId: ContractConst.SERVICEID_PRODUCT_PARTSMAN, productCode: ProductCode.Partsman },
          { serviceId: ContractConst.SERVICEID_PRODUCT_RECYCLE, productCode: ProductCode.Recycle },
          { serviceId: ContractConst.SERVICEID_OPT_MAINTENANCE_BASE, productCode: ProductCode.SuperFrontman },
          { serviceId: ContractConst.SERVICEID_OPT_REPAIR_BASE, productCode: ProductCode.SuperFrontman },
          { serviceId: ContractConst.SERVICEID_OPT_CMN_CARSALES_ESTIMATE_ORDER, productCode: ProductCode.CarSales },
          { serviceId: ContractConst.SERVICEID_PRODUCT_GLASS, productCode: ProductCode.Glass },
        ];
        const productCodeList = _filter(ProductDefList,
          d => !!services.find(s => s.serviceId === d.serviceId)
        ).map(d => d.productCode);

        if (productCodeList.length > 0) {
          return productCodeList;
        }
        return [ProductCode.SuperFrontman];
      })
    );
  }

  /**
   * 有効なOEMカスタマイズ情報を取得します。
   * @returns OEMカスタマイズ情報
   */
  getAvailableOemCustomize(): Observable<IOemCustomize> {
    return this.oemCustomizeService.getAvailableOemCustomize();
  }

  /**
   * 有効サービスID配列を取得します。
   * @returns 有効サービスID配列
   */
  getDetailAnalysisServiceIds() {
    return this.loginResourceService.getAllServiceAvailabilityInfo().pipe(
      map(response => {
        const list = [];
        response.forEach(info => {
          if (info.serviceAvailableFlag && _include(BiToolDetailAnalysisService, info.serviceId)) {
            list.push(info.serviceId);
          }
        });
        return list;
      })
    );
  }

  /**
   * 診断結果取得
   * @param params 実績集計検索条件
   * @returns 診断結果
   */
  getDiagnosisResultInfo(params: IActualTallyCondition): Observable<IDiagnosisResultInfo[]> {
    return this.diagnosisResultInfoResource.post<BlListResponseBody<IDiagnosisResultInfo>>(params).pipe(
      map(res => res ? res.searchResultList : []),
      catchError(
        (error: BlApiErrorInfo) => {
          // 存在しない場合はnullを返却
          if (error.status === 404) {
            return ObservableOf(null);
          }
          return ObservableThrow(error);
        }
      ),
    );
  }

  /**
   * 今月の実績結果取得
   * @param loginUser ログインユーザー情報
   * @returns 今月の実績結果
   */
  getThisMonthDiagnosisResultInfo(loginUser: ILoginUserEmployeeBindModel): Observable<IDiagnosisResultInfoEx> {
    const result: IDiagnosisResultInfoEx = {};
    let targetOrganizationCode = loginUser.organizationCode;

    const startDate = DateTimeUtils.thisMonth(DateDigitStyle.YYYYMMDD);
    const endDate = DateTimeUtils.getLastDayOfMonth(DateTimeUtils.today());
    const params: IActualTallyCondition = {
      tallyPeriodStDate: this.formatTallyPeriodDate(startDate),
      tallyPeriodEdDate: this.formatTallyPeriodDate(endDate),
      managementAnalysisSideMenu: ManagementAnalysisSideMenu.TargetAndSales
    };

    return this.getActualSearchSetting(loginUser.organizationCode, loginUser.employeeCode).pipe(
      mergeMap(setting => {
        if (!_isNil(setting)) {
          result.amountUnit = setting.amountUnit;

          params.organizationSumSetting = setting.organizationSumSetting;
          params.actualTallyOrganizationDiv = setting.actualTallyOrganizationDiv;
          params.amountUnit = setting.amountUnit;
          params.comparedToLastYear = setting.comparedToLastYear;

        } else {
          result.amountUnit = DisplayUnit.SenYen;

          params.organizationSumSetting = OrganizationSumSetting.Individual;
          params.actualTallyOrganizationDiv = ActualTallyOrganizationDiv.BillingAdd;
          params.amountUnit = DisplayUnit.SenYen;
          params.comparedToLastYear = ComparedToLastYear.None;
        }

        // 組織合算設定が全組織合算の場合、診断結果は全社(ORGANIZATION_CODE_ALL_COMPANY)で返るため、突合先を全社コードにする
        targetOrganizationCode = params.organizationSumSetting === OrganizationSumSetting.AllOrganizationSum
          ? CompanyConst.ORGANIZATION_CODE_ALL_COMPANY
          : loginUser.organizationCode;

        return this.getOrganizationSelectInfo(loginUser.organizationCode);
      }),
      mergeMap(orgInfo => {
        params.organizationSelectInfoList = [orgInfo];

        return this.loginResourceService.getAvailableProducts();
      }),
      mergeMap(products => {
        params.productCodeList = [];
        _forEach(products, p => {
          params.productCodeList.push(p.productCode);
        });
        params.managementReportServiceUseDiv = this.getManagementReportServiceUseDiv(params.productCodeList);

        // 入庫台数の実績を取得
        params.managementAnalysisItem = ManagementAnalysisItem.CarEnter;
        return this.getDiagnosisResultInfo(params);
      }),
      mergeMap(carEnterDiagnosis => {
        const findItem = _find(carEnterDiagnosis, d => d.sumDestinationOrganizationCode === targetOrganizationCode);
        result.actualCount = findItem ? findItem.actualCount : 0;
        result.goalVcleCnt = findItem ? findItem.goalVcleCnt : 0;

        // 売上の実績を取得
        params.managementAnalysisItem = ManagementAnalysisItem.Sales;
        return this.getDiagnosisResultInfo(params);
      }),
      map(salesDiagnosis => {
        if (salesDiagnosis) {
          const findItem = _find(salesDiagnosis, d => d.sumDestinationOrganizationCode === targetOrganizationCode);
          result.actualPriceTotal = findItem ? findItem.actualPriceTotal : 0;
          result.goalPriceTotal = findItem ? findItem.goalPriceTotal : 0;

          return result;
        } else {
          return null;
        }
      }),
    );
  }

  /**
   * 日付フォーマット（数値→ハイフン区切り日付）
   * @param date 数値日付
   * @returns ハイフン区切り日付
   */
  private formatTallyPeriodDate(date: number): string {
    return DateTimeUtils.format(date, DateTimeOutput.YMD, DateTimePadding.ZERO, DateSeparator.HYPHEN);
  }

  /**
   * 実績管理サービス利用区分取得
   * @param productCodeList プロダクトコードリスト
   * @returns 実績管理サービス利用区分
   */
  private getManagementReportServiceUseDiv(productCodeList: string[]) {
    if (productCodeList.length > 1) {
      return ProductCode.Common;
    } else {
      return productCodeList[0];
    }
  }

  /**
   * 組織選択情報取得
   * @param organizationCode 組織コード
   * @returns 組織選択情報
   */
  private getOrganizationSelectInfo(organizationCode: string): Observable<IOrganizationSelectInfo> {
    const result: IOrganizationSelectInfo = {};

    const conditions: BlConditionInfo[] = [
      { key: 'companyMgtCode', value: CommonConst.COMPANY_MGT_CODE_OWN_COMPANY, type: BlConditionType.Equal },
    ];

    return this.organizationInfoService.getAllWithIncludeLayer<IOrganizationInformation>({ conditions }).pipe(
      map(res => {
        const findOrg = _find(res.searchResultList, org => org.organizationCode === organizationCode);
        const findParent = this.getSumDestinationParent(findOrg, res.searchResultList);

        result.organizationCode = findOrg.organizationCode;
        result.organizationName = findOrg.organizationName;
        result.organizationLayerCode = findOrg.organizationLayerCode;
        result.sumDestinationParentOrganizationCode = findParent.organizationCode;
        result.sumDestinationParentOrganizationName = findParent.organizationName;
        result.sumDestinationParentOrganizationLayerCode = findParent.organizationLayerCode;

        return result;
      })
    );
  }

  /**
   * 親組織情報取得
   * @param org 組織情報
   * @param orgList 全ての組織情報リスト
   * @returns 親組織
   */
  private getSumDestinationParent(org: IOrganizationInformation, orgList: IOrganizationInformation[]): IOrganizationInformation {
    const findItem = _find(orgList, {
      organizationLayerCode: org.organizationLayerCode - 1,
      organizationCode: org.parentOrganizationCode,
    });
    if (!findItem) {
      // 取得できなくなったら終了
      return org;
    }

    // 次の親を探すため再帰処理
    return this.getSumDestinationParent(findItem, orgList);
  }

  /**
   * 検索設定情報取得
   * @param organizationCode 組織コード
   * @param employeeCode 従業員コード
   * @returns 検索設定情報
   */
  private getActualSearchSetting(organizationCode: string, employeeCode: string): Observable<IActualSearchSetting> {
    const entity: IActualSearchSetting = {
      organizationCode,
      employeeCode
    };
    const id = this.actualSearchSettingResource.getIdString(entity);

    return this.actualSearchSettingResource.getById(id).pipe(
      catchError((err: BlApiErrorInfo) => {
        if (err.status === HttpStatus.NotFound) {
          return ObservableOf(null);
        } else {
          throw err;
        }
      })
    );
  }

  /**
   * 電帳顧客一括同期呼び出し
   */
  public sendCustomerInfoToElectronicBookDx(): void {
    this.customerInfoResource.patch(null,
      { entityOptions: { exPath: 'electronicbookbulktransfer' } }
    ).pipe(
      catchError(() => {
        return ObservableOf(null);
      })
    ).subscribe();
  }

  /**
   * Yahoo注文情報_公開鍵確認
   * @returns Yahoo注文情報
   */
  getYahooOrderInfoPublicKeyValidationCheck(): Observable<IYahooOrderInfo> {
    return this.yahooOrderInfoResource.get<IYahooOrderInfo>(
      { entityOptions: { exPath: 'publicKeyValidationCheck' } }
    );
  }

  /**
   * YahooアプリケーションID設定_取得
   * @param organizationCode 組織コード
   * @returns YahooアプリケーションID設定
   */
  getYahooApplicationIdSetting(organizationCode: string): Observable<IYahooApplicationIdSetting> {
    const conditions = [
      {
        key: 'organizationCode',
        value: organizationCode,
        type: BlConditionType.Equal,
      },
    ];

    return this.yahooApplicationIdSettingResource.search<IYahooApplicationIdSetting>(
      { conditions: conditions },
      { consistent: true }
    ).pipe(
      map(response => response && response.searchResultList && response.searchResultList.length > 0 ? response.searchResultList[0] : null)
    );
  }

  /**
   * Yahoo在庫数量情報_差異発生確認
   */
  getYahooDiffExistFlag(): Observable<boolean> {
    return this.yahooStockQuantityInfoResource.get<boolean>(
      { entityOptions: { exPath: 'getyahoodiffexistflag' }, observe: 'response' }
    ).pipe(
      map(response => response.status === HttpStatus.Ok),
    );
  }

  /**
   * 受注メモ自動印刷
   */
  orderMemoAutoPrint(): Observable<any> {
    return this.ordermemoResource.post<any>(null,
      {
        entityOptions: {
          exPath: 'autoprint',
        },
      },
    );
  }

  /**
   * 受注メモ自動印刷エラー情報_取得
   * @param organizationCode 組織コード
   * @returns 受注メモ自動印刷エラー情報
   */
  getOrderMemoAutoPrintErrorInfo(organizationCode: string): Observable<IOrderMemoAutoPrintErrorInfo> {
    const conditions = [
      {
        key: 'organizationCode',
        value: organizationCode,
        type: BlConditionType.Equal,
      },
    ];

    return this.orderMemoAutoPrintErrorInfoResource.search<IOrderMemoAutoPrintErrorInfo>(
      { conditions: conditions },
      { subject: 'common', entityOptions: { exPath: 'search' } }
    ).pipe(
      map(response => response && response.searchResultList && response.searchResultList.length > 0 ? response.searchResultList[0] : null)
    );
  }

  /**
   * 受注メモ自動印刷可能かどうか
   * @param organizationCode 組織コード
   */
  isOrderMemoAutoPrintEmployee(organizationCode: string): Observable<boolean> {
    return this.onlineCompanySettingResource.getById<IOnlineCompanySetting>(
      organizationCode,
    ).pipe(
      catchError(
        (error: BlApiErrorInfo) => {
          switch (error.status) {
            case HttpStatus.NotFound:
              return ObservableOf(false);
            default:
              return ObservableThrow(error);
          }
        }
      ),
      map((setting: IOnlineCompanySetting) => {
        // 受注メモ自動印刷区分が2:印刷しないではない場合
        return setting && setting.orderMemoAutoPrintDiv !== OrderMemoAutoPrintDiv.None;
      })
    );
  }

  /**
   * PSユーザーコード登録済みか確認する
   */
  isRegisteredPsUserCode() {
    return RxForkJoin(
      this.loginResourceService.getLoginUserEmployeeBindModel(),
      this.loginResourceService.isAvailableService([ContractConst.SERVICEID_OPT_CMN_CONTACT_ORDER_USEDPART])
    ).pipe(
      mergeMap(
        ([lgoinUser, isAvailable]) => {
          if (!isAvailable) {
            return ObservableOf(false);
          }

          return this.onlineCompanySettingResource.getById<IOnlineCompanySetting>(
            lgoinUser.organizationCode,
            { entityOptions: { exPath: 'defaultdata' } }
          ).pipe(
            catchError(
              (error: BlApiErrorInfo) => {
                switch (error.status) {
                  case HttpStatus.NotFound:
                    return ObservableOf<IOnlineCompanySetting>({});
                  default:
                    return ObservableThrow(error);
                }
              }
            ),
            map(
              (setting: IOnlineCompanySetting) => setting && !!setting.psUserCode
            )
          );
        }
      )
    );
  }

  /**
   * ログインユーザの契約事業者情報を取得する
   * @returns
   */
  getContractCompanyInfo(): Observable<IContractCompanyInfo> {
    return this.contractCompanyInfoResource.getById('');
  }

  /**
  * BLPay(加盟店用）のバッジデータを取得する
  * @param contractInquiryId 契約管理照会ID
  * @returns
  */
  getBlPayAffiliatedCount(contractInquiryId: string): Observable<number> {
    const params = new BlApiQueryParams();
    const conditions = [
      BlApiConditionHelper.makeCondStrEqual('affiliatedStoreSuppliersDiv', AffiliatedStoreSuppliersDiv.Suppliers),
      BlApiConditionHelper.makeCondStrEqual('affiliatedStoreContractInquiryId', contractInquiryId),
      BlApiConditionHelper.makeCondStrEqual('verifySuppliers', ApplyStatus.Apply)
    ];
    const apiCondition = new BlApiSearchCondition();
    apiCondition.addCondition(conditions);
    params.setCondition(apiCondition);
    const searchParam = {
      params,
      entityOptions: {
        exPath: 'search_count',
      },
    };
    return this.blPayApplyInfoResource.get<{ totalCount: number }>(searchParam)
      .map(
        response => {
          if (_isEmpty(response)) {
            return 0;
          } else {
            return response.totalCount;
          }
        }
      );
  }


  /**
  * BLPay(取引先用）のバッジデータを取得する
  * @param contractInquiryId 契約管理照会ID
  * @returns
  */
  getBlPaySuppliersCount(contractInquiryId: string): Observable<number> {
    const params = new BlApiQueryParams();
    const conditions = [
      BlApiConditionHelper.makeCondStrEqual('affiliatedStoreSuppliersDiv', AffiliatedStoreSuppliersDiv.Suppliers),
      BlApiConditionHelper.makeCondStrEqual('suppliersContractInquiryId', contractInquiryId),
      BlApiConditionHelper.makeCondStrEqual('applyStatusDiv', ContractApplyStatusDiv.Remanded)
    ];
    const apiCondition = new BlApiSearchCondition();
    apiCondition.addCondition(conditions);
    params.setCondition(apiCondition);
    const searchParam = {
      params,
      entityOptions: {
        exPath: 'search_count',
      },
    };
    return this.blPayApplyInfoResource.get<{ totalCount: number }>(searchParam)
      .map(
        response => {
          if (_isEmpty(response)) {
            return 0;
          } else {
            return response.totalCount;
          }
        }
      );
  }

  /**
   * ローカルストレージから入金画面タイプを読み込みます。
   * @returns 入金画面タイプ
   */
  public loadLocalStorageDepositScreenType(): string {
    const mode = LocalStorageUtils.get(LocalStorageKey.DepositScreenType);
    // ローカルストレージが読み込めない場合、入金登録（入金型）に遷移する
    return mode ? mode.toString() : DepositScreenType.Deposit.code;
  }

  /**
   * ローカルストレージから出金画面タイプを読み込みます。
   * @returns 出金画面タイプ
   */
  public loadLocalStoragePaymentScreenType(): string {
    const mode = LocalStorageUtils.get(LocalStorageKey.PaymentScreenType);
    // ローカルストレージが読み込めない場合、出金登録（出金型）に遷移する
    return mode ? mode.toString() : PaymentScreenType.Payment.code;
  }

  /**
   * ログインテナントのタスクステータス取得します。
   * @returns スクステータス
   */
  getTenlantTaskStatus(): Observable<IBiToolTask[]> {
    return this.loginResourceService.getLoginUserEmployeeBindModel().pipe(
      map(({ blTenantId }) => {
        return blTenantId;
      }), flatMap(blTenantId => {
        const conditions = new BlApiSearchCondition();
        conditions.addCondition(
          BlApiConditionHelper.makeCondAnd(
            BlApiConditionHelper.makeCondStrIn('biToolBlTenantId', [blTenantId]),
          ),
        );
        const result = this.biToolTaskResource.search<IBiToolTask>(
          { conditions: conditions },
          { subject: 'common', entityOptions: { exPath: 'conditon/search' } }
        );
        return result.pipe(map(res => res ? res.searchResultList : []));
      })
    );
  }

  /**
   * 通知案内情報を取得します。
   * @returns 通知案内情報
   */
  getNotificationGuide(): Observable<INotificationMessage[]> {
    // 7日前から当日までを対象
    // 通知案内日時が本日より後、かつ通知案内日時が（本日＋7日）より前
    // ※0時0分になるため(本日+8)のLessThanで判定する
    const targetDateStart = DateTimeUtils.today();
    const targetDateEnd = DateTimeUtils.addDays(targetDateStart, 8);

    const conditions = [
      {
        key: 'logicaldeletediv',
        value: LogicalDeleteDiv.Valid,
        type: BlConditionType.Equal,
      },
      {
        key: 'notificationExpirationDate',
        value: DateTimeUtils.formatIso(DateTimeUtils.now()),
        type: BlConditionType.GreaterThanEqual,
      },
      {
        key: 'notificationLevel',
        value: NotificationLevel.Warning,
        type: BlConditionType.Equal,
      },
      {
        key: 'notificationGuideDiv',
        value: NotificationGuideDiv.Guide,
        type: BlConditionType.Equal,
      },
      {
        key: 'notificationGuideDateTime',
        value: DateTimeUtils.formatIso(targetDateStart),
        type: BlConditionType.GreaterThanEqual,
      },
      {
        key: 'notificationGuideDateTime',
        value: DateTimeUtils.formatIso(targetDateEnd),
        type: BlConditionType.LessThan,
      },
    ];

    const fields = [
      'notificationId',
      'notificationTitle',
      'notificationContent'
    ];

    return this.notificationMessageResource.getAll<INotificationMessage>({ conditions, fields }).pipe(
      map((response) => {
        return response ? response.searchResultList : [];
      })
    );
  }

  /**
   * アラート用の通知案内情報を取得します。
   * @returns 通知案内情報
   */
  getNotificationGuideForAlert(): Observable<INotificationMessage[]> {
    // 通知案内日時が本日より後
    const targetDateStart = DateTimeUtils.today();

    const conditions = [
      {
        key: 'logicaldeletediv',
        value: LogicalDeleteDiv.Valid,
        type: BlConditionType.Equal,
      },
      {
        key: 'notificationExpirationDate',
        value: DateTimeUtils.formatIso(DateTimeUtils.now()),
        type: BlConditionType.GreaterThanEqual,
      },
      {
        key: 'notificationLevel',
        value: [NotificationLevel.Alert, NotificationLevel.Critical],
        type: BlConditionType.In
      },
      {
        key: 'notificationGuideDiv',
        value: NotificationGuideDiv.Guide,
        type: BlConditionType.Equal,
      },
      {
        key: 'notificationGuideDateTime',
        value: DateTimeUtils.formatIso(targetDateStart),
        type: BlConditionType.GreaterThanEqual,
      },
    ];

    const fields = [
      'notificationTitle',
      'notificationContent',
      'notificationLevel'
    ];

    const sortFields = [
      {
        field: 'notificationSendDateTime',
        desc: true
      },
      {
        field: 'notificationId',
        desc: false
      },
    ];

    return this.notificationMessageResource.getAll<INotificationMessage>({ conditions, fields, sortFields }).pipe(
      map((response) => {
        return response ? response.searchResultList : [];
      })
    );
  }

  /**
   * ローカルストレージからログイン後初回フラグを読み込みます。
   * @returns ログイン後初回フラグ
   */
  loadLocalStorageInitAfterLogin(): boolean {
    const flag = LocalStorageUtils.get(LocalStorageKey.InitAfterLogin);
    return flag && typeof flag === 'boolean' ? flag : false;
  }

  /**
   * ローカルストレージのログイン後初回フラグを削除します。
   */
  removeLocalStorageInitAfterLogin(): void {
    LocalStorageUtils.remove(LocalStorageKey.InitAfterLogin);
  }

  /**
   * 未手配アラート通知
   * @param availabilities 権限配列
   */
  notArrangedNotice(availabilities?: string[]) {
    if (availabilities) {
      this.availabilitiesCache = availabilities;
      return;
    }

    let url = ''; // 遷移先URL

    // Ma / Pmの同居はないのでオプション状態の相互チェック不要
    if (this.availabilitiesCache.includes(ContractConst.SERVICEID_OPT_PARTSMAN_BASE)) {
      // PMだと、PMの予約連携のサービスIDチェックを行う。
      if (this.availabilitiesCache.includes(ContractConst.SERVICEID_OPT_PMC_RESERVE_LINK)) {
        url = UrlUtils.buildUrlPath('/pm/search/reservation'); // 部品商
      }
    } else {
      // PM以外だと、SERVICEID_OPT_CMN_RESRVE_PUBLISH（予約公開サービス）チェックを行う。
      if (this.availabilitiesCache.includes(ContractConst.SERVICEID_OPT_CMN_RESRVE_PUBLISH)) {
        url = UrlUtils.buildUrlPath('/reserve/entry#2'); // 整備
      }
    }

    // なし：何もしない
    if (_isEmpty(url)) {
      return;
    }

    // 画面通知パラメータ
    const screenNotify: ScreenNotifyParam = {
      /** タイトル */
      title: '手配が必要な予約があります。',
      /** メッセージ */
      messages: ['未手配の予約が存在します。'],
      /** リンク情報 */
      linkInfos: [{
        /** リンクテキスト */
        text: '予約一覧を確認する',
        /** 遷移先URL */
        url
      }]
    };
    // 未手配アラート通知API呼び出す
    this.reserveSearchResource.get<boolean>({ entityOptions: { exPath: 'notarrangednotice' }, observe: 'response' }).pipe(
      map(response => response.status === HttpStatus.Ok),
      catchError(() => ObservableOf(false))
    ).subscribe(
      (isShow) => {
        if (isShow) { // HttpStatus.Ok
          this.screenNotifyService.warn(screenNotify).subscribe(() => { });
        }
      },
    );
  }

  /**
   * 未購入部品アラート通知を表示する
   * @param count カート件数
   */
  notifyNotPurchasedParts(count: number) {
    if (this.purchasedPartsToastId) {
      this.screenNotifyService.removeId(this.purchasedPartsToastId);
    }

    if (!count) {
      return;
    }

    this.screenNotifyService.warn({
      /** タイトル */
      title: '未購入の部品が存在します。',
      /** メッセージ */
      messages: [`カートに${count}件の部品が存在します。`],
      /** リンク情報 */
      linkInfos: [{
        /** リンクテキスト */
        text: 'カートを確認する',
        /** 遷移先URL */
        url: UrlUtils.buildUrlPath('/order/mp/cart'),
        /** 別タブ起動 */
        blank: true,
      }]
    }).subscribe(
      notifyResult => {
        this.purchasedPartsToastId = notifyResult.toast.id;
      }
    );
  }

  /**
   * 受発注の未購入部品数(カート追加済部品数)を取得する
   */
  getNotPurchasedParts(): Observable<number> {
    return this.orderPartsCartResource.getById<IOrderPartsCart>('call').pipe(
      catchError(() => ObservableOf(null)), // エラーは0件として扱う
      map(
        cart => {
          if (_isEmpty(cart)) {
            return 0;
          }

          return cart.orderPartsCartDetailsList.length;
        }
      )
    );
  }

  /**
   * 受発注のお気に入り登録数を取得する
   */
  getMPFavoriteCount(): Observable<number> {
    return this.partsOrderFavoriteDisplayResource.getAll<IPartsOrderFavoriteDisplay>(
      null,
      { entityOptions: { exPath: 'q/search-favorite-info' } }
    ).pipe(
      catchError(() => ObservableOf(null)), // エラーは0件として扱う
      map(
        response => response && !_isEmpty(response.searchResultList) ? response.searchResultList.length : 0
      )
    );
  }

  /**
   * 受発注・BLPのチャット未読数を取得する
   * * APIのクエリ等はチャット一覧と全く同じになるようにする
   * @param organizationCode 組織コード
   * @returns 未読数
   */
  getUnreadInquiryPartsChatCount(organizationCode: string): Observable<number> {
    const today = DateTimeUtils.formatIso(DateTimeUtils.today());
    const startDate = DateTimeUtils.formatIso(DateTimeUtils.addMonths(DateTimeUtils.today(), -1));
    const conditions: BlConditionInfo[] = [
      { key: 'updateDateTime', value: DateTimeUtils.toDateTime(startDate), type: BlConditionType.GreaterThanEqual },
      { key: 'updateDateTime', value: this.toEndOfDay(today), type: BlConditionType.LessThanEqual },
      { key: 'buyerOrganizationCode', value: [organizationCode], type: BlConditionType.In }
    ];

    return this.chatListDisplayInfoResource.search<IChatListDisplayInfo>(
      {
        conditions,
        sortFields: [
          { field: 'updateDateTime', desc: true },
        ],
        size: 100,
        page: 1,
      }
    ).pipe(
      catchError(() => ObservableOf<BlListResponseBody<IChatListDisplayInfo>>(null)), // エラーは0件として扱う
      map(
        response => {
          if (_isEmpty(response) || _isEmpty(response.searchResultList)) {
            return 0;
          }

          let unreadCount = 0;
          _forEach(response.searchResultList, (chat) => {
            if (chat.chatDisplayInfo.messageNewArrivalsNotifyFlag) {
              unreadCount++;
            }
          });
          return unreadCount;
        }
      )
    );
  }

  /**
   * 日付を一日の終わりの日時(23:59:59)に変換する
   * @param date 日付
   * @returns 一日の終わりの時刻
   */
  private toEndOfDay(date: string) {
    return DateTimeUtils.toDateTime(date, '2359').replace(/:00$/, ':59');
  }

  /**
   * 共通汎用フラグを取得します。
   * @param key 汎用フラグキー
   * @returns 共通汎用フラグ
   */
  getCommonGenericFlagMgt(key: string): Observable<boolean> {
    return this.commonGenericFlagMgtResource.getById<ICommonGenericFlagMgt>(key).pipe(
      map(response => {
        return response ? response.genericFlagValue : false;
      }),
      catchError(() => {
        return ObservableOf(true);
      })
    );
  }

  /**
   * 請求書設定を取得する
   * @param organizationCode 組織コード
   * @returns 請求書設定
   */
  getBillSetting(organizationCode: string): Observable<IBillSetting> {
    return this.billSettingResource.getById(organizationCode);
  }

  /**
   * 帳票レイアウト_機能性検索処理
   * @param pageId 画面ID
   * @param reportUseCode 帳票用途コード
   * @returns 帳票レイアウト設定
   */
  getExportReportLayoutSetting(pageId: string): Observable<IReportLayoutSetting> {
    const conditions: BlConditionInfo[] = [
      { key: 'pageId', value: pageId, type: BlConditionType.Equal },
      { key: 'reportUseCode', value: this.reportUseCode, type: BlConditionType.Equal }
    ];
    const sortFields = [{ field: 'reportId', desc: false }];
    return this.reportLayoutSettingResource
      .getAll<IReportLayoutSetting>({ conditions, sortFields })
      .pipe(
        map((response) => {
          return _get(response, 'searchResultList[0]', null);
        })
      );
  }

    /**
   * 出力パターン情報を取得します。
   * @param exportInfoTabDiv 出力情報タブ区分
   * @param exportInfoDiv 出力情報区分
   * @param exportWorkTypeDiv 出力作業タイプ区分
   * @returns 出力パターン情報
   */
  getExportPatternInfo(
    exportInfoTabDiv: string,
    exportInfoDiv: string,
    exportWorkTypeDiv: string,
    exportPatternCode: string
  ): Observable<IExportPatternInfo[]> {

    const andCondition = [
      BlApiConditionHelper.makeCondStrEqual('logicalDeleteDiv', LogicalDeleteDiv.Valid),
      BlApiConditionHelper.makeCondStrEqual('exportInfoTabDiv', exportInfoTabDiv),
    ];

    if (!_isEmpty(exportInfoDiv)) {
      andCondition.push(
        BlApiConditionHelper.makeCondStrEqual('exportInfoDiv', exportInfoDiv),
      );
    }

    if (!_isEmpty(exportWorkTypeDiv)) {
      andCondition.push(
        BlApiConditionHelper.makeCondStrEqual('exportWorkTypeDiv', exportWorkTypeDiv),
      );
    }

    if (!_isEmpty(exportPatternCode)) {
      andCondition.push(
        BlApiConditionHelper.makeCondStrEqual('exportPatternCode', exportPatternCode),
      );
    }

    const conditions = new BlApiSearchCondition();
    conditions.addCondition(
      BlApiConditionHelper.makeCondAnd(...andCondition)
    );

    const sortFields = [
      {
        field: 'exportPatternCode',
        desc: false,
      }
    ];
    return this.exportPatternInfoResource.getAll<IExportPatternInfo>({ conditions, sortFields }).pipe(
      map(res => res && res.searchResultList ? res.searchResultList : [])
    );
  }

  /**
   * ローカルストレージに出力パターン選択情報を保存します。
   * @param blTenantId テナントID
   * @param blUserId ユーザーID
   * @param exportInfoTabDiv 出力情報タブ区分
   * @param exportPatterns 出力パターン選択情報
   */
  saveExportPatternLocalStorage(blTenantId: string, blUserId: string, exportInfoTabDiv: string, exportPatterns: any) {
    const storageKey = this.getExportPatternStorageKey(blTenantId, blUserId, exportInfoTabDiv);
    LocalStorageUtils.save(storageKey, exportPatterns);
  }

  /**
   * ローカルストレージから出力パターン選択情報を取得します。
   * @param blTenantId テナントID
   * @param blUserId ユーザーID
   * @param exportInfoTabDiv 出力情報タブ区分
   * @returns 出力パターン選択情報
   */
  getExportPatternLocalStorage(blTenantId: string, blUserId: string, exportInfoTabDiv: string): any {
    const storageKey = this.getExportPatternStorageKey(blTenantId, blUserId, exportInfoTabDiv);
    let exportPatterns = LocalStorageUtils.get(storageKey);
    if (!exportPatterns) {
      exportPatterns = {};
    }
    return exportPatterns;
  }


  /**
   * 出力パターンのストレージキーを取得します。
   * @param blTenantId テナントID
   * @param blUserId ユーザーID
   * @param exportInfoTabDiv 出力情報タブ区分
   * @returns 出力パターンのストレージキー
   */
  private getExportPatternStorageKey(blTenantId: string, blUserId: string, exportInfoTabDiv: string) {
    return 'exportPattern_' + blTenantId.toUpperCase() + '-' + blUserId + '-' + exportInfoTabDiv;
  }

  /**
   * データ出力確認画面を表示し、データ出力処理を行います
   * @param currentTabId 選択中タブID
   */
  export(currentTabId: string) {
    // 出力条件画面
    let modalComponent: Type<any> = CreditExportConditionModalComponent;
    // 画面ID
    let pageId = PageId.MenuDepositAndBilling + '-Pm';
    // 操作履歴機能名称
    let opeHistoryFunctionName = '請求売掛残高一覧';
    // 項目ID
    let itemnID = 'billingAccRecBalExtractCondition';

    if (currentTabId === 'debt') {
      modalComponent = DebtExportConditionModalComponent;
      pageId = PageId.MenuWithdrawalAndPayment + '-Pm';
      opeHistoryFunctionName = '支払買掛残高一覧';
      itemnID = 'accpayBalanceExtractCondition';
    }

    this.modalService.show(modalComponent, {
      size: ModalSize.Large,
    }).subscribe(
      modalRef => {
        modalRef.hide();
        if (modalRef.reason === ModalReason.Done) {
          const event = modalRef.getResults();
          this.exportProc(event['condition'], pageId, opeHistoryFunctionName, itemnID);
        }
      }
    );
  }

  /**
   * データ出力確認画面を表示し、データ出力処理を行います
   * @param extractCondition 請求売掛残高出力条件
   * @param pageId 画面ID
   * @param opeHistoryFunctionName 操作履歴機能名称
   * @param itemId 項目ID
   */
  exportProc(
    extractCondition: IBillingAccRecBalExtractCondition | IAccpayBalanceExtractCondition,
    pageId: string,
    opeHistoryFunctionName: string,
    itemId: string
  ) {
    if (itemId === 'billingAccRecBalExtractCondition') {
      // 売掛の場合
      if (extractCondition.balanceType === BalanceType.ACCREC) {
        pageId = pageId + '-1';
      }
    } else {
      pageId = this.pageIdEdit(extractCondition, pageId);
    }
    const exportModalData = this.getExportModalData(extractCondition, pageId, itemId);
    this.getExportReportLayoutSetting(pageId)
      .pipe(
        switchMap((reportLayoutSetting) => {
          // 帳票レイアウト設定
          if (!reportLayoutSetting || _isEmpty(reportLayoutSetting)) {
            return this.dialogService.warn('帳票設定情報が存在しないため、処理を中止します。')
              .pipe(tap((ref) => ref.hide()));
          }

          exportModalData.reportUseCode = this.reportUseCode;
          exportModalData.reportId = String(reportLayoutSetting.reportId);

          // データ出力確認
          this.modalService
            .show(ExportRequestConfirmComponent)
            .subscribe(async (subModalRef) => {
              if (subModalRef.reason === ModalReason.Done) {
                this.loadingService.show();

                // 出力実行
                this.doPrint(exportModalData)
                  .pipe(finalize(() => this.loadingService.hide()))
                  .subscribe(
                    (response) => {
                      // 操作ログ登録
                      this.postOperationHistoryLog(opeHistoryFunctionName);
                      // データ出力後処理
                      if (itemId === 'billingAccRecBalExtractCondition') {
                        // 請求・売掛の場合
                        this.afterExportRequest(response, false);
                      } else {
                        // 支払・買掛の場合
                        this.afterExportRequest(response, true);
                      }
                    },
                    (error: BlApiErrorInfo) => {
                      // エラーハンドリング
                      this.handleExportRequestError(error, opeHistoryFunctionName);
                    }
                  );
              }
              subModalRef.hide();
            });
          return empty();
        })
      )
      .subscribe();
  }

  /**
   * データ出力モーダルパラメータを取得する
   * @param extractCondition 請求売掛残高出力条件
   * @param debtFlag 支払・買掛フラグ
   * @returns データ出力モーダルパラメータ
   */
  private getExportModalData(
    extractCondition: IBillingAccRecBalExtractCondition | IAccpayBalanceExtractCondition,
    pageId: string,
    itemID: string
  ): IGenericPrintModalData {
    const data: IGenericPrintModalData = {
      // 帳票関連情報を取得するために使用する画面ID ※必須
      pageId: pageId,
      // 個別の印刷APIがデータを取得するために使用する条件
      genericPrintConditionList: [
        {
          itemnID: itemID,
          extractConditionString: JSON.stringify(extractCondition)
        }
      ],
      respondablePrintErrorStatuses: [404],
    };
    return data;
  }

  /**
   * 印刷処理を実行
   * @param param 印刷データ
   */
  doPrint(param: IGenericPrintInstruction): Observable<any> {
    return this.setReportLayoutSetting(param)
      .pipe(
        mergeMap((reportLayoutParam) => {
          return this.getPrintPositionSetting(reportLayoutParam);
        }),
        mergeMap((genericPrintInstruction) => {
          return this.genericPrintInstructionResource.put<IGenericPrintInstruction>(
            genericPrintInstruction, { observe: 'response', entityOptions: { exPath: 'print' } }
          );
        })
      );
  }

  /**
   * 帳票レイアウト設定
   * @param param 印刷設定
   */
  setReportLayoutSetting(param: IGenericPrintInstruction): Observable<IGenericPrintInstruction> {
    return this.reportLayoutSettingResource.get<IReportLayoutSetting>(
      {
        entityOptions: { id: param.reportId + '_' + param.pageId + '_' + param.reportUseCode }
      }
    ).pipe(
      map((layoutSetting: IReportLayoutSetting) => {
        param.reportOutDiv = ReportOutputDiv.Pdf;
        param.reportLayoutSetting = layoutSetting;
        return param;
      })
    );
  }

  /**
  * 印字位置設定を取得
  * @param param 印刷設定
  */
  getPrintPositionSetting(param: IGenericPrintInstruction): Observable<IGenericPrintInstruction> {
    // 印字位置設定を取得
    const id = this.printPositionSettingResource.getIdString({ reportId: param.reportId, printerId: null, printPositionBranchNumber: 0 });
    return this.printPositionSettingResource.getById<IPrintPositionSetting>(id)
      .pipe(map(positionSetting => {
        param.printPositionSetting = positionSetting;
        return param;
      }));
  }

  /**
   * 操作履歴ログを登録します。
   * @param opeHistoryFunctionName 操作履歴機能名称
   */
  protected postOperationHistoryLog(opeHistoryFunctionName: string) {
    this.loginResourceService.getLoginUserEmployeeBindModel().subscribe(
      loginUser => {
        const entity: IOpeHistory = {
          productCode: ProductCode.Partsman,
          logicalDeleteDiv: LogicalDeleteDiv.Valid,
          functionCategoryDiv: FunctionCategoryDiv.Entry,
          blTenantId: loginUser.blTenantId,
          organizationCode: loginUser.organizationCode,
          organizationName: loginUser.organizationName,
          createEmployeeCode: loginUser.employeeCode,
          createEmployeeName: loginUser.employeeName,
          opeHistoryFunctionName: opeHistoryFunctionName,
          opeDiv: OpeDiv.DataOutput,
          opeHistoryDtlValueList: [`出力データ：${this.exportInfoName}`],
        };

        this.opeHistoryResource.post<IOpeHistory>(entity).pipe(
          catchError(() => {
            return ObservableOf(null);
          })
        ).subscribe();

      });
  }

  /**
   * データ出力リクエスト後処理
   * @param _response レスポンス
   * @param debtflag 支払・買掛フラグ
   */
  protected afterExportRequest(_response: any, debtflag: boolean) {
    let message = `請求・売掛PDFの出力を開始しました。\nPDF出力状況画面で処理状況を確認して下さい。`;
    if (debtflag) {
      message = `支払・買掛PDFの出力を開始しました。\nPDF出力状況画面で処理状況を確認して下さい。`;
    }

    this.dialogService.info(message).subscribe(
      (dialogRef) => {
        dialogRef.hide();
      }
    );
  }

  /**
   * エラーハンドリング
   * @param error エラー
   * @param opeHistoryFunctionName 操作履歴機能名称
   */
  protected handleExportRequestError(error: BlApiErrorInfo, opeHistoryFunctionName: string) {
    if (error.status === HttpStatus.Unauthorized || error.status === HttpStatus.Forbidden) {
      // 認証と権限エラーはスロー
      throw error;
    }

    let message: string;
    if (!_isNil(error) && !_isEmpty(error.messages)) {
      message = StringUtils.concatWith('\n', ...error.messages);
    }
    if (_isEmpty(message)) {
      message = `${opeHistoryFunctionName}PDF出力処理に失敗しました。`;
    }

    // サービス側ではあるがダイアログ表示を行う。
    this.dialogService.error(message).subscribe((dialogRef) => {
      dialogRef.hide();
    });
  }

  /**
   * 支払・買掛の支払先別・仕入先組織別PDF出力の場合、ファイル名出し分けのため画面IDを編集
   * @param extractCondition 請求売掛残高出力条件
   * @param pageId 画面ID
   * @returns 変更した画面ID
   */
  protected pageIdEdit(extractCondition: IAccpayBalanceExtractCondition, pageId: string
  ) {
    // 支払の場合
    if (extractCondition.balanceType === BalanceType.PAY) {
      // 仕入先組織別
      if (extractCondition.accPayBalanceDataOutputType === AccPayBalanceDataOutputType.SupplierOrg) {
        pageId = pageId + '-1';
      }
    // 買掛の場合
    } else if (extractCondition.balanceType === BalanceType.ACCPAY) {
      // 支払先別
      if (extractCondition.accPayBalanceDataOutputType === AccPayBalanceDataOutputType.Payee) {
        pageId = pageId + '-2';
      // 仕入先組織別
      } else {
        pageId = pageId + '-3';
      }
    }
    return pageId;
  }

}



// WEBPACK FOOTER //
// ./src/app/page/biz-menu-page/biz-menu-page.service.ts